import SEO from './../components/SEO';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Pencil, Trash2, Save, X, RefreshCw,
  CheckCircle, AlertCircle, Building2, Upload, Zap, Lock,
} from 'lucide-react';
import { listProfiles, createProfile, updateProfile, deleteProfile } from '../services/api';
import { useEntitlements } from '../hooks/useEntitlements';
import UpgradeModal from '../components/UpgradeModal';

// ── Shared style constants ───────────────────────────────────────────────────

const inputCls =
  'w-full px-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white ' +
  'border-gray-300 dark:border-gray-700 placeholder-gray-400 focus:outline-none ' +
  'focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-colors';

const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const EMPTY_FORM = {
  profile_name: '', business_name: '', business_email: '', business_phone: '',
  business_address: '', business_city: '', business_state: '', business_zip: '',
  business_country: '', business_gstin: '', business_vat_number: '', business_logo: null,
  invoice_number_prefix: '', invoice_number_padding: '',
};

const PADDINGS = [
  { v: '', label: 'Default (from Settings)' },
  { v: 3, label: '3 digits (001)' },
  { v: 4, label: '4 digits (0001)' },
  { v: 5, label: '5 digits (00001)' },
  { v: 6, label: '6 digits (000001)' },
];

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

// ── Toast component ──────────────────────────────────────────────────────────

function Toast({ toast, onClose }) {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all ${
        isSuccess
          ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
          : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
      }`}
    >
      {isSuccess
        ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
        : <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-1 opacity-60 hover:opacity-100 transition-opacity">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── Small sub-components ─────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
}

// ── Profile card (list view) ─────────────────────────────────────────────────

function ProfileCard({ profile, onEdit, onDelete, deleting }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 flex gap-4 hover:shadow-md transition-shadow">
      {/* Logo / placeholder */}
      <div className="flex-shrink-0 w-14 h-14 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
        {profile.business_logo
          ? <img src={profile.business_logo} alt="logo" className="w-full h-full object-contain p-1" />
          : <Building2 className="w-6 h-6 text-gray-300 dark:text-gray-600" />}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{profile.profile_name}</p>
        {profile.business_name && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{profile.business_name}</p>
        )}
        {(profile.business_email || profile.business_phone) && (
          <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
            {[profile.business_email, profile.business_phone].filter(Boolean).join(' · ')}
          </p>
        )}
        {(profile.business_gstin || profile.business_vat_number) && (
          <div className="flex gap-1.5 mt-1.5">
            {profile.business_gstin && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded">
                GST
              </span>
            )}
            {profile.business_vat_number && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded">
                VAT
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        {confirmDelete ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-gray-600 dark:text-gray-400 text-right">Delete?</p>
            <div className="flex gap-1.5">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 px-2.5 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setConfirmDelete(false); onDelete(profile); }}
                disabled={deleting === profile.id}
                className="flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                {deleting === profile.id
                  ? <RefreshCw className="w-3 h-3 animate-spin" />
                  : 'Yes'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={() => onEdit(profile)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={deleting === profile.id}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Profile form (create / edit) ─────────────────────────────────────────────

function ProfileForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  const [saving, setSaving] = useState(false);
  const [profileNameError, setProfileNameError] = useState('');
  const [formError, setFormError] = useState('');
  const logoRef = useRef(null);
  const profileNameRef = useRef(null);

  const set = (key, val) => {
    if (key === 'profile_name' && profileNameError) setProfileNameError('');
    setForm(f => ({ ...f, [key]: val }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) { setFormError('Logo must be under 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => set('business_logo', ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.profile_name.trim()) {
      setProfileNameError('Profile name is required.');
      profileNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      profileNameRef.current?.querySelector('input')?.focus();
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await onSave(form);
      // onSave resolves → parent switches view; no need to setSaving(false) here
    } catch (err) {
      setFormError(err.message || 'Failed to save profile');
      setSaving(false);
    }
  };

  const isEdit = !!initial?.id;

  const SaveBtn = ({ className }) => (
    <button
      onClick={handleSubmit}
      disabled={saving}
      className={className}
    >
      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      {saving ? 'Saving…' : 'Save Profile'}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span className="text-sm">Profiles</span>
            </button>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEdit ? 'Edit Profile' : 'New Profile'}
            </h1>
          </div>
          <SaveBtn className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-6">
        {/* Network / server error banner */}
        {formError && (
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">{formError}</p>
          </div>
        )}

        {/* Profile Identity */}
        <div
          ref={profileNameRef}
          className={`bg-white dark:bg-gray-900 rounded-xl border p-6 space-y-4 transition-colors ${
            profileNameError
              ? 'border-red-400 dark:border-red-600'
              : 'border-gray-200 dark:border-gray-800'
          }`}
        >
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Profile Identity</h2>
          <Field label="Profile Name *" hint='A label for yourself, e.g. "My Agency" or "Freelance Studio"'>
            <input
              type="text"
              className={`${inputCls} ${profileNameError ? 'border-red-400 dark:border-red-600 focus:ring-red-500/40 focus:border-red-500' : ''}`}
              placeholder="e.g. My Agency"
              value={form.profile_name}
              onChange={e => set('profile_name', e.target.value)}
              autoFocus
            />
            {profileNameError && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {profileNameError}
              </p>
            )}
          </Field>
        </div>

        {/* Logo */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Business Logo</h2>
          <div className="flex items-start gap-5">
            {form.business_logo ? (
              <div className="relative w-24 h-24 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800 flex-shrink-0">
                <img src={form.business_logo} alt="Logo" className="w-full h-full object-contain p-1" />
                <button
                  onClick={() => set('business_logo', null)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center bg-gray-50 dark:bg-gray-800 flex-shrink-0">
                <Building2 className="w-8 h-8 text-gray-300 dark:text-gray-600" />
              </div>
            )}
            <div>
              <button
                onClick={() => logoRef.current?.click()}
                className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
              >
                <Upload className="w-4 h-4" /> Upload Logo
              </button>
              <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">PNG, JPG, SVG up to 2 MB</p>
              <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Business Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Business Name">
              <input type="text" className={inputCls} placeholder="Acme Consulting" value={form.business_name} onChange={e => set('business_name', e.target.value)} />
            </Field>
            <Field label="Business Email">
              <input type="email" className={inputCls} placeholder="hello@acme.com" value={form.business_email} onChange={e => set('business_email', e.target.value)} />
            </Field>
            <Field label="Phone">
              <input type="tel" className={inputCls} placeholder="+1 555 000 0000" value={form.business_phone} onChange={e => set('business_phone', e.target.value)} />
            </Field>
            <Field label="Country">
              <input type="text" className={inputCls} placeholder="United States" value={form.business_country} onChange={e => set('business_country', e.target.value)} />
            </Field>
          </div>
          <Field label="Street Address">
            <input type="text" className={inputCls} placeholder="123 Main Street" value={form.business_address} onChange={e => set('business_address', e.target.value)} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="City">
              <input type="text" className={inputCls} placeholder="New York" value={form.business_city} onChange={e => set('business_city', e.target.value)} />
            </Field>
            <Field label="State / Province">
              <input type="text" className={inputCls} placeholder="NY" value={form.business_state} onChange={e => set('business_state', e.target.value)} />
            </Field>
            <Field label="ZIP / Postal">
              <input type="text" className={inputCls} placeholder="10001" value={form.business_zip} onChange={e => set('business_zip', e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Tax Identifiers */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Tax Identifiers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="GSTIN" hint="GST Identification Number (India)">
              <input type="text" className={inputCls} placeholder="22AAAAA0000A1Z5" value={form.business_gstin} onChange={e => set('business_gstin', e.target.value)} />
            </Field>
            <Field label="VAT Number" hint="VAT registration number (EU / UK / global)">
              <input type="text" className={inputCls} placeholder="GB123456789" value={form.business_vat_number} onChange={e => set('business_vat_number', e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Invoice Number Format */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Invoice Number Format</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Override the global invoice number format for invoices created under this profile.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Custom Prefix" hint="Leave blank to use the global setting (e.g. INV, GST-INV)">
              <input
                type="text"
                maxLength={30}
                className={inputCls}
                placeholder="e.g. ACME-2025"
                value={form.invoice_number_prefix}
                onChange={e => set('invoice_number_prefix', e.target.value)}
              />
            </Field>
            <Field label="Digit Padding" hint="Leave at default to inherit the global setting">
              <select
                value={form.invoice_number_padding}
                onChange={e => set('invoice_number_padding', e.target.value === '' ? '' : Number(e.target.value))}
                className={inputCls}
              >
                {PADDINGS.map(p => (
                  <option key={p.v} value={p.v}>{p.label}</option>
                ))}
              </select>
            </Field>
          </div>
          {form.invoice_number_prefix && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Preview:</span>
              <span className="font-mono font-semibold text-blue-700 dark:text-blue-400 text-sm">
                {form.invoice_number_prefix}-{'1'.padStart(Number(form.invoice_number_padding) || 6, '0')}
              </span>
            </div>
          )}
        </div>

        {/* Save footer */}
        <div className="flex justify-end pb-8">
          <SaveBtn className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors shadow-sm" />
        </div>
      </main>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null = list view | 'new' | profile obj
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(false);
  const { isProUser } = useEntitlements();
  const atProfileLimit = !isProUser && profiles.length >= 1;

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    let active = true;
    listProfiles()
      .then(data => { if (active) setProfiles(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const handleEdit = useCallback((profile) => {
    setEditing(profile);
  }, []);

  const handleNew = useCallback(() => {
    if (atProfileLimit) { setUpgradeModal(true); return; }
    setEditing('new');
  }, [atProfileLimit]);

  const handleSave = useCallback(async (formData) => {
    const payload = {
      ...formData,
      invoice_number_padding: formData.invoice_number_padding === '' ? null : formData.invoice_number_padding,
    };
    if (editing === 'new') {
      const created = await createProfile(payload);
      setProfiles(prev => [created, ...prev]);
      setEditing(null);
      showToast('Profile created successfully!', 'success');
    } else {
      const updated = await updateProfile(editing.id, payload);
      setProfiles(prev => prev.map(p => p.id === editing.id ? updated : p));
      setEditing(null);
      showToast('Profile saved!', 'success');
    }
  }, [editing, showToast]);

  const handleDelete = useCallback(async (profile) => {
    setDeleting(profile.id);
    try {
      await deleteProfile(profile.id);
      setProfiles(prev => prev.filter(p => p.id !== profile.id));
      showToast('Profile deleted.', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete profile', 'error');
    } finally {
      setDeleting(null);
    }
  }, [showToast]);

  // ── Form view ──────────────────────────────────────────────────────────────
  if (editing !== null) {
    return (
      <ProfileForm
        initial={editing === 'new' ? null : editing}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO title="Profile | Invilic" description="Manage your Invilic profile." canonical="https://www.invilic.com/profile" noindex={true} />
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline text-sm">Home</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Business Profiles
            </h1>
          </div>
          <button
            onClick={handleNew}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm ${
              atProfileLimit
                ? 'bg-amber-500 hover:bg-amber-600'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {atProfileLimit ? <Zap className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            <span className="hidden sm:inline">{atProfileLimit ? 'Upgrade to Add' : 'Add Profile'}</span>
            <span className="sm:hidden">{atProfileLimit ? 'Upgrade' : 'Add'}</span>
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-5">
        {/* Info banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
          Saved profiles appear as a quick-fill option when you create a new invoice. Pick one and your business details are filled in instantly.
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && profiles.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No profiles yet</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-xs">
              Add your business details once and reuse them across all your invoices.
            </p>
            <button
              onClick={handleNew}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" /> Add your first profile
            </button>
          </div>
        )}

        {/* Profile list */}
        {!loading && profiles.length > 0 && (
          <div className="space-y-3">
            {profiles.map(profile => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                onEdit={handleEdit}
                onDelete={handleDelete}
                deleting={deleting}
              />
            ))}
            {/* Add another button — gated for free plan */}
            {atProfileLimit ? (
              <button
                onClick={() => setUpgradeModal(true)}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-xl text-sm font-medium text-amber-600 dark:text-amber-400 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20 transition-colors"
              >
                <Lock className="w-4 h-4" />
                Multiple profiles is a Pro feature
                <span className="ml-1 text-[10px] font-bold bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded-full">Upgrade</span>
              </button>
            ) : (
              <button
                onClick={handleNew}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add another profile
              </button>
            )}
          </div>
        )}
      </main>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {upgradeModal && (
        <UpgradeModal
          reason="Multiple business profiles is a Pro feature. Upgrade to create unlimited profiles."
          onClose={() => setUpgradeModal(false)}
        />
      )}
    </div>
  );
}
